import React, { useState } from 'react'
import { Button, CheckboxInput, InputModal } from '../../../lib/components'
import {
  NDA_ACCEPTANCE_CHECKBOX_LABELS,
  NdaConfidentialityLabel,
} from '../../../lib/utils/nda-acceptance'
import {
  getAnswerOptionIds,
  isQuestionAnswered,
  isSkipLimitSelected,
  RISK_PROFILER_QUESTIONS,
  RISK_PROFILER_SKIP_LIMIT_DISCLAIMER,
  RiskProfilerAnswers,
  RiskQuestionIcon,
} from '../../../lib/utils/risk-profiler'
import { AcceptNDAText } from '../../accept-nda/accept-nda-text'

interface RiskProfilerPresenterProps {
  currentStep: number
  answers: RiskProfilerAnswers
  onSelectOption: (questionId: string, optionId: string) => void
  onBack: () => void
  onNext: () => void
  onSubmit: () => void
  isSubmitting: boolean
  acceptedConfidentiality: boolean
  acceptedNoOffer: boolean
  acceptedKycDocuments: boolean
  acceptedAdverseMedia: boolean
  onAcceptanceChange: (
    field:
      | 'acceptedConfidentiality'
      | 'acceptedNoOffer'
      | 'acceptedKycDocuments'
      | 'acceptedAdverseMedia',
    value: boolean,
  ) => void
}

const QuestionIcon = ({ type }: { type: RiskQuestionIcon }) => {
  const common = {
    width: 40,
    height: 40,
    viewBox: '0 0 40 40',
    fill: 'none',
    'aria-hidden': true as const,
  }

  switch (type) {
    case 'target':
      return (
        <svg {...common}>
          <circle cx='20' cy='20' r='14' stroke='currentColor' strokeWidth='2' />
          <circle cx='20' cy='20' r='8' stroke='currentColor' strokeWidth='2' />
          <circle cx='20' cy='20' r='2' fill='currentColor' />
        </svg>
      )
    case 'volatility':
      return (
        <svg {...common}>
          <path
            d='M6 28 L14 20 L20 26 L28 12 L34 16'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path d='M28 12 L34 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
          <path d='M28 12 L34 16' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
        </svg>
      )
    case 'horizon':
      return (
        <svg {...common}>
          <circle cx='20' cy='22' r='12' stroke='currentColor' strokeWidth='2' />
          <path d='M20 22 L20 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
          <path d='M20 22 L26 26' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
        </svg>
      )
    case 'experience':
      return (
        <svg {...common}>
          <rect x='8' y='10' width='24' height='20' rx='2' stroke='currentColor' strokeWidth='2' />
          <path
            d='M12 16 H28 M12 22 H22'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      )
    case 'reaction':
      return (
        <svg {...common}>
          <path
            d='M8 26 C12 18 16 30 20 22 C24 14 28 26 32 18'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      )
    case 'allocation':
      return (
        <svg {...common}>
          <path d='M8 30 V18 H16 V30' stroke='currentColor' strokeWidth='2' />
          <path d='M16 30 V14 H24 V30' stroke='currentColor' strokeWidth='2' />
          <path d='M24 30 V10 H32 V30' stroke='currentColor' strokeWidth='2' />
        </svg>
      )
    case 'liquidity':
      return (
        <svg {...common}>
          <path
            d='M20 8 C20 8 10 20 10 26 C10 31.5 14.5 34 20 34 C25.5 34 30 31.5 30 26 C30 20 20 8 20 8 Z'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinejoin='round'
          />
        </svg>
      )
    case 'knowledge':
      return (
        <svg {...common}>
          <path
            d='M12 28 V14 C12 12 14 10 18 10 H22 C26 10 28 12 28 14 V28'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
          <path d='M20 10 V28' stroke='currentColor' strokeWidth='2' />
          <path d='M10 28 H30' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
        </svg>
      )
    case 'understanding':
      return (
        <svg {...common}>
          <circle cx='20' cy='20' r='12' stroke='currentColor' strokeWidth='2' />
          <path
            d='M16 18 C16 15 18 14 20 14 C22 14 24 15 24 18 C24 21 20 21 20 24'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
          <circle cx='20' cy='28' r='1.5' fill='currentColor' />
        </svg>
      )
    default:
      return null
  }
}

const RiskProfilerPresenter = ({
  currentStep,
  answers,
  onSelectOption,
  onBack,
  onNext,
  onSubmit,
  isSubmitting,
  acceptedConfidentiality,
  acceptedNoOffer,
  acceptedKycDocuments,
  acceptedAdverseMedia,
  onAcceptanceChange,
}: RiskProfilerPresenterProps) => {
  const [isNdaDocOpen, setIsNdaDocOpen] = useState(false)
  const totalSteps = RISK_PROFILER_QUESTIONS.length
  const question = RISK_PROFILER_QUESTIONS[currentStep]
  const selectedOptionIds = getAnswerOptionIds(answers[question.id])
  const progressPercent = ((currentStep + 1) / totalSteps) * 100
  const isLastStep = currentStep === totalSteps - 1
  const canGoNext = isQuestionAnswered(question, answers)
  const hasAcceptedAllStatements =
    acceptedConfidentiality && acceptedNoOffer && acceptedKycDocuments && acceptedAdverseMedia
  const canSubmit = canGoNext && hasAcceptedAllStatements && !isSubmitting

  const optionCount = question.options.length
  const hasOddLastOption = optionCount % 2 === 1

  return (
    <div className='risk-profiler' data-testid='risk-profiler'>
      <div
        className='risk-profiler__progress'
        role='progressbar'
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label='Progress'
      >
        <div className='risk-profiler__progress-fill' style={{ width: `${progressPercent}%` }} />
      </div>

      <div className='risk-profiler__question-block'>
        <div className='risk-profiler__icon-wrap'>
          <QuestionIcon type={question.icon} />
        </div>
        <h3 className='risk-profiler__question' data-testid='risk-profiler-question'>
          {question.question}
        </h3>
        {question.guidance ? (
          <p className='risk-profiler__guidance' data-testid='risk-profiler-guidance'>
            {question.guidance}
          </p>
        ) : null}
      </div>

      <div className='risk-profiler__options-layout'>
        <button
          type='button'
          className='risk-profiler__nav-btn risk-profiler__nav-btn--prev'
          onClick={onBack}
          disabled={currentStep === 0}
          aria-label='Previous question'
          data-testid='risk-profiler-back-btn'
        >
          ‹
        </button>

        <div
          className={`risk-profiler__options-grid ${
            hasOddLastOption ? 'risk-profiler__options-grid--odd-last' : ''
          }`}
          role={question.multiSelect ? 'group' : 'listbox'}
          aria-label={question.question}
        >
          {question.options.map((option, index) => {
            const isSelected = selectedOptionIds.includes(option.id)
            const isLastAlone = hasOddLastOption && index === optionCount - 1

            return (
              <button
                key={option.id}
                type='button'
                role={question.multiSelect ? 'checkbox' : 'option'}
                aria-checked={question.multiSelect ? isSelected : undefined}
                aria-selected={question.multiSelect ? undefined : isSelected}
                aria-describedby={option.description ? `risk-option-help-${option.id}` : undefined}
                className={`risk-profiler__option ${
                  isSelected ? 'risk-profiler__option--selected' : ''
                } ${isLastAlone ? 'risk-profiler__option--centered' : ''} ${
                  option.description ? 'risk-profiler__option--with-help' : ''
                } ${question.multiSelect ? 'risk-profiler__option--multi' : ''}`}
                onClick={() => onSelectOption(question.id, option.id)}
                data-testid={`risk-profiler-option-${option.id}`}
              >
                {question.multiSelect ? (
                  <span className='risk-profiler__option-check' aria-hidden='true' />
                ) : null}
                <span className='risk-profiler__option-label'>{option.label}</span>
                {option.description ? (
                  <span
                    id={`risk-option-help-${option.id}`}
                    className='risk-profiler__option-help'
                    role='tooltip'
                  >
                    {option.description}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>

        {!isLastStep && !question.multiSelect ? (
          <button
            type='button'
            className='risk-profiler__nav-btn risk-profiler__nav-btn--next'
            onClick={onNext}
            disabled={!canGoNext}
            aria-label='Next question'
            data-testid='risk-profiler-next-btn'
          >
            ›
          </button>
        ) : (
          <span className='risk-profiler__nav-spacer' aria-hidden='true' />
        )}
      </div>

      {question.multiSelect && !isLastStep ? (
        <div className='risk-profiler__actions risk-profiler__actions--next'>
          <p className='risk-profiler__next-hint' data-testid='risk-profiler-multi-next-hint'>
            After selecting your options, tap Next to continue.
          </p>
          <Button
            text='Next'
            classes={['auto-btn-width']}
            variant='primary-btn'
            handleClick={onNext}
            isDisabled={!canGoNext}
            testId='risk-profiler-next-btn'
          />
        </div>
      ) : null}

      {isLastStep && isSkipLimitSelected(answers) ? (
        <p className='risk-profiler__skip-disclaimer' data-testid='risk-profiler-skip-disclaimer'>
          {RISK_PROFILER_SKIP_LIMIT_DISCLAIMER}
        </p>
      ) : null}

      {isLastStep && (
        <div className='risk-profiler__acceptance'>
          <div className='risk-profiler__acceptance-item'>
            <CheckboxInput
              labelText={<NdaConfidentialityLabel onOpenDocument={() => setIsNdaDocOpen(true)} />}
              name='acceptedConfidentiality'
              value={acceptedConfidentiality ? 'true' : 'false'}
              handleChange={(val) =>
                onAcceptanceChange('acceptedConfidentiality', val.target.checked)
              }
              dataTestId='risk-profiler-nda-confidentiality-checkbox'
            />
          </div>
          <div className='risk-profiler__acceptance-item'>
            <CheckboxInput
              labelText={NDA_ACCEPTANCE_CHECKBOX_LABELS.noOffer}
              name='acceptedNoOffer'
              value={acceptedNoOffer ? 'true' : 'false'}
              handleChange={(val) => onAcceptanceChange('acceptedNoOffer', val.target.checked)}
              dataTestId='risk-profiler-nda-no-offer-checkbox'
            />
          </div>
          <div className='risk-profiler__acceptance-item'>
            <CheckboxInput
              labelText={NDA_ACCEPTANCE_CHECKBOX_LABELS.kycDocuments}
              name='acceptedKycDocuments'
              value={acceptedKycDocuments ? 'true' : 'false'}
              handleChange={(val) => onAcceptanceChange('acceptedKycDocuments', val.target.checked)}
              dataTestId='risk-profiler-kyc-documents-checkbox'
            />
          </div>
          <div className='risk-profiler__acceptance-item'>
            <CheckboxInput
              labelText={NDA_ACCEPTANCE_CHECKBOX_LABELS.adverseMedia}
              name='acceptedAdverseMedia'
              value={acceptedAdverseMedia ? 'true' : 'false'}
              handleChange={(val) => onAcceptanceChange('acceptedAdverseMedia', val.target.checked)}
              dataTestId='risk-profiler-adverse-media-checkbox'
            />
          </div>
          <div className='risk-profiler__actions'>
            <Button
              text={isSubmitting ? 'Saving…' : 'Submit'}
              classes={['auto-btn-width']}
              variant='primary-btn'
              handleClick={onSubmit}
              isDisabled={!canSubmit}
              testId='risk-profiler-submit-btn'
            />
          </div>
        </div>
      )}

      {isNdaDocOpen && (
        <InputModal
          testId='risk-profiler-nda-doc-modal'
          onBackdropClick={() => setIsNdaDocOpen(false)}
        >
          <div className='risk-profiler__nda-doc-modal' role='dialog' aria-modal='true'>
            <h3 className='risk-profiler__nda-doc-title'>Confidentiality Agreement</h3>
            <div className='risk-profiler__nda-doc-body'>
              <AcceptNDAText />
            </div>
            <div className='risk-profiler__nda-doc-actions'>
              <Button
                text='Close'
                classes={['auto-btn-width']}
                variant='primary-btn'
                handleClick={() => setIsNdaDocOpen(false)}
                testId='risk-profiler-nda-doc-close'
              />
            </div>
          </div>
        </InputModal>
      )}
    </div>
  )
}

export { RiskProfilerPresenter }
